import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {EntityInfoComponent, getSuperclassEntityType, isDocumentEntity, isFileObject,} from '|shared';
import {ApiSettlementService, DocumentDto, FileDto} from '|api/document';
import {InternalNotificationKey} from '|api/commons';
import {
  ButtonComponent,
  FormButtonsComponent,
  IconComponent,
  LoadingIndicatorService,
  TooltipDirective,
  WaitingDirective
} from '@icz/angular-essentials';
import {
  DatePickerComponent,
  FormFieldComponent,
  IczDateValidators,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczValidators
} from '@icz/angular-form-elements';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {ReactiveFormsModule} from '@angular/forms';
import {AsyncPipe} from '@angular/common';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable
} from '@icz/angular-application-core';
import {DocumentToastService, DocumentToastType, FileToastService, FileToastType} from '|ui-essl-core';

export interface SuspendOrResumeSettlementDialogData {
  isSuspend: boolean,
  selectedObjects: (DocumentDto | FileDto)[],
}

@Component({
  selector: 'icz-suspend-or-resume-settlement-dialog',
  templateUrl: './suspend-or-resume-settlement-dialog.component.html',
  styleUrls: ['./suspend-or-resume-settlement-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IczFormContainerDirective, WaitingDirective, EntityInfoComponent, DatePickerComponent, FormFieldComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe, TooltipDirective, IconComponent]
})
export class SuspendOrResumeSettlementDialogComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  protected modalRef = injectModalRef<Nullable<boolean>>();
  protected checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private apiSettlementService = inject(ApiSettlementService);
  private documentToastService = inject(DocumentToastService);
  private fileToastService = inject(FileToastService);
  protected data = injectModalData<SuspendOrResumeSettlementDialogData>();

  form = new IczFormGroup({
    date: new IczFormControl<string>((new Date()).toISOString(), [IczValidators.required()]),
    reason: new IczFormControl<Nullable<string>>(null, [IczValidators.required()]),
    renewalDeadline: new IczFormControl<Nullable<number>>(null, this.isFileSuspension ? [IczValidators.isInteger()] : []),
  });

  formGroupsToCheck!: string[];

  isBulkOperation = false;

  selectableDates = IczDateValidators.pastOrPresentDateValidator;

  get isFileSuspension() {
    return this.data.isSuspend && this.data.selectedObjects.some(isFileObject);
  }

  ngOnInit() {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);
    this.isBulkOperation = this.data.selectedObjects.length > 1;
  }

  submit() {
    const requestData = {
      ...this.form.getRawValue(),
      operationEntities: this.data.selectedObjects.map(obj => ({
        entityType: getSuperclassEntityType(obj.entityType!),
        id: obj.id!
      }))};

    if (this.data.isSuspend) {
      this.loadingService.doLoading(
        this.apiSettlementService.settlementSuspendSettlements({
          body: {
            date: new Date(requestData.date).toISOString(),
            reason: requestData.reason!,
            renewalDeadline: requestData.renewalDeadline,
            entitiesForSuspend: requestData.operationEntities
          }
        }),
        this
      ).subscribe(_ => {
        if (this.isBulkOperation) {
          this.documentToastService.dispatchBulkOperationStartedToast(DocumentToastType.BULK_SETTLEMENT_SUSPEND_STARTED, {
            [InternalNotificationKey.COUNT]: this.data.selectedObjects.length
          });
        } else {
          if (isDocumentEntity(this.data.selectedObjects[0].entityType)) {
            this.documentToastService.dispatchDocumentInfoToast(DocumentToastType.SETTLEMENT_SUSPEND_STARTED, {
              [InternalNotificationKey.DOCUMENT_ID]: this.data.selectedObjects[0].id!,
              [InternalNotificationKey.DOCUMENT_SUBJECT]: this.data.selectedObjects[0].subject
            });
          } else {
            this.fileToastService.dispatchFileInfoToast(FileToastType.FILE_SETTLEMENT_SUSPEND_STARTED, {
              [InternalNotificationKey.FILE_ID]: this.data.selectedObjects[0].id!,
              [InternalNotificationKey.FILE_SUBJECT]: this.data.selectedObjects[0].subject
            });
          }
        }
        this.form.markAsPristine();
        this.modalRef.close(true);
      });
    } else {
      this.loadingService.doLoading(
        this.apiSettlementService.settlementResumeSettlements({
          body: {
            date: new Date(requestData.date).toISOString(),
            reason: requestData.reason!,
            entitiesForResume: requestData.operationEntities
          }
        }),
        this
      ).subscribe(_ => {
        if (this.isBulkOperation) {
          this.documentToastService.dispatchBulkOperationStartedToast(DocumentToastType.BULK_SETTLEMENT_RESUME_STARTED, {
            [InternalNotificationKey.COUNT]: this.data.selectedObjects.length
          });
        } else {
          if (isDocumentEntity(this.data.selectedObjects[0].entityType)) {
            this.documentToastService.dispatchDocumentInfoToast(DocumentToastType.SETTLEMENT_RESUME_STARTED, {
              [InternalNotificationKey.DOCUMENT_ID]: this.data.selectedObjects[0].id!,
              [InternalNotificationKey.DOCUMENT_SUBJECT]: this.data.selectedObjects[0].subject
            });
          } else {
            this.fileToastService.dispatchFileInfoToast(FileToastType.FILE_SETTLEMENT_RESUME_STARTED, {
              [InternalNotificationKey.FILE_ID]: this.data.selectedObjects[0].id!,
              [InternalNotificationKey.FILE_SUBJECT]: this.data.selectedObjects[0].subject
            });
          }
        }
        this.form.markAsPristine();
        this.modalRef.close(true);
      });
    }
  }

  cancel() {
    this.modalRef.close(null);
  }

}
